// Realistic sample data per document type, shaped exactly like what the
// backend injects into a print (`window.__ZP_DATA__`), so formulas in the
// builder preview give believable numbers.
import { getDocType, STORE_FIELD_LIST } from "./doc-types";

const SAMPLE_ITEMS = [
  { name: "Paracetamol 650mg Tablet", generic: "Paracetamol", code: "ITM0012", hsn: "30049099", pack: "1 x 15", mfr: "Micro Labs Ltd", mfrCode: "MCL", qty: 2, free: 0, price: 32.5, disc: 0, gst: 12, uom: "Strip", rack: "R2-S3", sched: "", batch: "MPL2401", exp: "31/08/2027", mfg: "01/09/2025" },
  { name: "Azithromycin 500mg Tablet", generic: "Azithromycin", code: "ITM0457", hsn: "30042019", pack: "1 x 3", mfr: "Cipla Ltd", mfrCode: "CPL", qty: 3, free: 1, price: 119.0, disc: 5, gst: 12, uom: "Strip", rack: "R4-S1", sched: "H", batch: "AZ5521", exp: "30/04/2027", mfg: "01/05/2025" },
  { name: "Vitamin C Chewable 500mg", generic: "Ascorbic Acid", code: "ITM0881", hsn: "21069099", pack: "1 x 30", mfr: "Abbott India", mfrCode: "ABT", qty: 1, free: 0, price: 245.0, disc: 10, gst: 18, uom: "Bottle", rack: "R1-S5", sched: "", batch: "VC9013", exp: "31/12/2026", mfg: "01/01/2025" },
  { name: "ORS Orange Sachet 21g", generic: "Oral Rehydration Salts", code: "ITM1120", hsn: "30049011", pack: "1 x 1", mfr: "FDC Ltd", mfrCode: "FDC", qty: 10, free: 0, price: 21.0, disc: 0, gst: 5, uom: "Sachet", rack: "R3-S2", sched: "", batch: "OR2207", exp: "30/06/2027", mfg: "01/07/2025" },
];

const r2 = (n) => Math.round(n * 100) / 100;

function buildLine(s, i, idPrefix) {
  const gross = s.qty * s.price;
  const discAmt = r2((gross * s.disc) / 100);
  const taxable = r2(gross - discAmt);
  const tax = r2((taxable * s.gst) / 100);
  return {
    item_id: `${idPrefix}-item-${i + 1}`,
    item_code: s.code,
    item_name: s.name,
    item_generic_name: s.generic,
    item_hsn: s.hsn,
    item_pack_size: s.pack,
    item_manufacturer_id: `mfr-${s.mfrCode.toLowerCase()}`,
    item_manufacturer_name: s.mfr,
    item_mfg: s.mfr,
    item_mfg_code: s.mfrCode,
    mfg_code: s.mfrCode,
    item_uom: s.uom,
    item_uom_id: `uom-${s.uom.toLowerCase()}`,
    item_rack_location: s.rack,
    item_schedule: s.sched,
    item_batch_number: s.batch,
    item_exp_date: s.exp,
    item_mfg_date: s.mfg,
    item_quantity: s.qty,
    item_free_quantity: s.free,
    item_received_quantity: s.qty,
    item_to_be_delivered: s.qty,
    item_to_be_received: s.qty,
    base_quantity: s.qty,
    item_unit_price: s.price.toFixed(2),
    mrp: (s.price * 1.12).toFixed(2),
    molecule_name: s.generic,
    item_discount_percentage: s.disc,
    item_discount_amount: discAmt.toFixed(2),
    item_price_wiithout_tax: r2(s.price / (1 + s.gst / 100)).toFixed(2),
    item_gst: s.gst,
    item_total_tax_percentage: s.gst,
    item_cgst: s.gst / 2,
    item_sgst: s.gst / 2,
    item_igst: 0,
    item_tax_amount: tax.toFixed(2),
    item_total_amount: r2(taxable + tax).toFixed(2),
    item_uom_source: s.uom,
    item_uom_destination: s.uom,
    linked_invoice_number: "INV/26-27/00412",
    sot_invoice_number: "INV/26-27/00412",
    _taxable: taxable,
    _tax: tax,
  };
}

function buildBatches(line, kind) {
  const half = Math.max(1, Math.floor(line.item_quantity / 2));
  const parts = line.item_quantity > 2 ? [half, line.item_quantity - half] : [line.item_quantity];
  return parts.map((q, i) => {
    const b = {
      item_id: line.item_id,
      item_batch_number: i === 0 ? line.item_batch_number : line.item_batch_number.slice(0, -1) + "9",
      item_exp_date: line.item_exp_date,
      item_mfg_date: line.item_mfg_date,
      item_batch_quantity: q,
      item_batch_free_quantity: i === 0 ? line.item_free_quantity : 0,
      item_batch_unit_price: line.item_unit_price,
      item_batch_discount_percentage: line.item_discount_percentage,
      item_batch_discount_amount: r2((Number(line.item_discount_amount) * q) / line.item_quantity).toFixed(2),
      item_batch_tax_percentage: line.item_gst,
      item_batch_tax_amount: r2((Number(line.item_tax_amount) * q) / line.item_quantity).toFixed(2),
      item_batch_cgst_amount: r2((Number(line.item_tax_amount) * q) / line.item_quantity / 2).toFixed(2),
      item_batch_sgst_amount: r2((Number(line.item_tax_amount) * q) / line.item_quantity / 2).toFixed(2),
      item_batch_igst_amount: "0.00",
      from_bin_location: line.item_rack_location,
      to_bin_location: "QC-01",
      from_warehouse_name: "Main Store",
      to_warehouse_name: "Kukatpally Branch",
    };
    if (kind === "purchase") {
      const rate = r2(Number(line.item_unit_price) * 0.78);
      Object.assign(b, {
        item_sellable_quantity: q,
        item_non_sellable_quantity: 0,
        item_batch_purchase_rate: rate.toFixed(2),
        item_batch_final_purchase_rate: r2(rate * (1 + line.item_gst / 100)).toFixed(2),
        item_batch_total_purchase_rate: r2(rate * q * (1 + line.item_gst / 100)).toFixed(2),
        item_batch_taxable_amount: r2(rate * q).toFixed(2),
        item_batch_margin_percentage: 22,
      });
    } else {
      Object.assign(b, {
        item_batch_purchase_rate: r2(Number(line.item_unit_price) * 0.78).toFixed(2),
        item_batch_sales_rate: line.item_unit_price,
        item_batch_total_sales_rate: r2(Number(line.item_unit_price) * q).toFixed(2),
        item_batch_final_sales_rate: r2((Number(line.item_total_amount) * q) / line.item_quantity).toFixed(2),
      });
    }
    return b;
  });
}

function taxSummary(lines) {
  const by = {};
  lines.forEach((l) => {
    const k = l.item_gst;
    if (!by[k]) by[k] = { item_gst: k, taxable: 0, gst: 0, sgst: 0, igst: 0 };
    by[k].taxable = r2(by[k].taxable + l._taxable);
    by[k].gst = r2(by[k].gst + l._tax / 2);
    by[k].sgst = r2(by[k].sgst + l._tax / 2);
  });
  return Object.values(by);
}

export const SAMPLE_STORE = {
  store_id: "store-001",
  store_code: "HYD-001",
  company: "Zota Health Care Pvt Ltd",
  company_alias_name: "Zota Pharmacy",
  company_address: "Plot 42, Road No. 12, Banjara Hills",
  company_area: "Banjara Hills",
  company_landmark: "Opp. City Center Mall",
  company_city: "Hyderabad",
  company_state: "Telangana",
  company_pincode: "500034",
  company_email: "billing@zotapharmacy.in",
  company_telephone1: "040-23456789",
  company_telephone2: "9876543210",
  company_gstn: "36AABCZ1234F1Z5",
  company_licence: "TS/HYD/2024/1234",
  company_logo: "",
  pan_number: "AABCZ1234F",
  dl_no_20_20b: "TS/HYD/20B/2024/101",
  dl_no_21_21b: "TS/HYD/21B/2024/102",
  dl_no_21c: "TS/HYD/21C/2024/103",
  dl_no_21f: "",
  fssai_no: "13624008000123",
  company_remarks: "Thank you for shopping with us!",
  company_terms_conditions: "1. Goods once sold will not be taken back.\n2. Subject to Hyderabad jurisdiction.",
  company_return_policy: "Returns accepted within 7 days with the original bill.",
};

const PARTY = {
  cmr_id: "cmr-0042",
  cmr_code: "C0042",
  cmr_name: "Sri Sai Medical Agencies",
  cmr_phone_number: "9849012345",
  cmr_first_name: "Ravi",
  cmr_last_name: "Kumar",
  cmr_company: "Sri Sai Medical Agencies",
  cmr_gst: "36AAFFS5678K1ZQ",
  cmr_pan: "AAFFS5678K",
  cmr_state: "Telangana",
  cmr_area: "Kukatpally, Hyderabad",
  cmr_rewards: 120,
};

function docNumber(key) {
  const p = {
    billing_invoice: "INV", billing_return: "CRN", sales_order: "SO", delivery_note: "DN", sales_invoice: "SI",
    sales_return: "SR", sales_credit_note: "SCN", purchase_order: "PO", good_receipt_note: "GRN",
    purchase_invoice: "PI", purchase_return: "PR", purchase_credit_note: "PCN", stock_transfer: "ST",
  }[key] || "DOC";
  return `${p}/26-27/00412`;
}

export function buildSampleData(docTypeKey) {
  const dt = getDocType(docTypeKey);
  const p = dt.prefix;
  const isPurchase = dt.group === "Purchase";
  const lines = SAMPLE_ITEMS.map((s, i) => buildLine(s, i, p));
  if (dt.batch) lines.forEach((l) => (l.itemBatchData = buildBatches(l, isPurchase ? "purchase" : "sales")));

  const sub = r2(lines.reduce((a, l) => a + l.item_quantity * Number(l.item_unit_price), 0));
  const disc = r2(lines.reduce((a, l) => a + Number(l.item_discount_amount), 0));
  const gst = r2(lines.reduce((a, l) => a + l._tax, 0));
  const exact = r2(sub - disc + gst);
  const total = Math.round(exact);
  const roundOff = r2(total - exact);
  const number = docNumber(dt.key);

  const order = { ...PARTY, created_date: "16/10/2026", update_date: "16/10/2026", gst_type: "Intra", remarks: "Deliver before 5 PM" };
  if (dt.key === "stock_transfer") {
    Object.assign(order, {
      itt_id: "itt-0001", stock_transfer_doc_number: number, transfer_date: "16/10/2026", posting_date: "16/10/2026",
      stock_management_action: "Transfer", from_warehouse_id: "wh-01", from_warehouse_name: "Main Store",
      to_warehouse_id: "wh-02", to_warehouse_name: "Kukatpally Branch", stock_transfer_reason: "Stock rebalancing",
      stock_transfer_status: "Completed",
    });
  } else {
    Object.assign(order, {
      [`${p}_id`]: `${p}-0001`,
      [`${p}_invoice_number`]: number,
      [`${p}_custom_invoice_number`]: number,
      inv_no: number,
      invoice_number: number,
      customer_invoice_number: "VEN/8812",
      [`${p}_sub_total`]: sub.toFixed(2),
      [`${p}_total_discount`]: disc.toFixed(2),
      [`${p}_total_gst`]: gst.toFixed(2),
      [`${p}_round_off_value`]: roundOff.toFixed(2),
      round_off: roundOff.toFixed(2),
      round_off_value: roundOff.toFixed(2),
      [`${p}_total_amount`]: total.toFixed(2),
      [`${p}_payment_status`]: "Paid",
      [`${p}_payment_method`]: "Cash",
      [`${p}_order_status`]: "Completed",
      [`${p}_transaction_id`]: "TXN784512",
      [`${p}_billing_address`]: "Plot 7, KPHB Colony, Kukatpally, Hyderabad - 500072",
      [`${p}_ship_to_address`]: "Plot 7, KPHB Colony, Kukatpally, Hyderabad - 500072",
      [`${p}_pay_to_address`]: "Plot 7, KPHB Colony, Kukatpally, Hyderabad - 500072",
      [`${p}_gst_number`]: PARTY.cmr_gst,
      [`${p}_document_date`]: "16/10/2026",
      [`${p}_delivery_date`]: "18/10/2026",
      [`${p}_due_date`]: "15/11/2026",
      [`${p}_order_date`]: "14/10/2026",
      [`${p}_expected_delivery_date`]: "20/10/2026",
      [`${p}_reference_inv_number`]: "REF/2026/118",
      [`${p}_reference_inv_date`]: "10/10/2026",
      [`${p}_remarks`]: "Get well soon",
      [`${p}_invoice_discount`]: "0.00",
      document_date: "16/10/2026",
      doctor_name: "Dr. Anitha Rao",
      created_by: "Likith",
      tds_tcs_type: "TCS",
      tcs_or_tds_percentage: 0.1,
      tcs_or_tds_amount: r2(total * 0.001).toFixed(2),
      pcns_total: "0.00",
      remaining_balance: "0.00",
    });
  }

  const data = {
    order,
    store: { ...SAMPLE_STORE },
    payments: dt.payments
      ? [
          { payment_mode_name: "Cash", payment_amount: (total - 200).toFixed(2) },
          { payment_mode_name: "UPI", payment_amount: "200.00" },
        ]
      : [],
    items: lines.map(({ _taxable, _tax, ...rest }) => rest),
    tax: dt.key === "stock_transfer" ? [] : taxSummary(lines),
    extra: {},
  };
  if (dt.creditNotes) {
    data.extra.creditNotes = [
      { invoice_number: "PCN/26-27/0007", invoice_date: "02/10/2026", invoice_total_amount: "1200.00", claimed_amount: "1200.00", adjusted_amount: "450.00", remaining_amount: "750.00", remarks: "Expiry return" },
    ];
  }
  if (dt.key === "stock_transfer") {
    data.extra.fromStore = { store_code: "HYD-001", firm_name: "Zota Pharmacy - Main Store", firm_address: "Plot 42, Road No. 12", firm_area: "Banjara Hills", firm_city: "Hyderabad", firm_state: "Telangana", firm_pin_code: "500034" };
    data.extra.toStore = { store_code: "HYD-007", firm_name: "Zota Pharmacy - Kukatpally", firm_address: "Shop 3, KPHB Main Road", firm_area: "Kukatpally", firm_city: "Hyderabad", firm_state: "Telangana", firm_pin_code: "500072" };
  }
  return data;
}

// Grouped field catalog for the builder's Fields panel.
export function fieldCatalog(docTypeKey) {
  const dt = getDocType(docTypeKey);
  const groups = [
    { id: "order", title: "Document", root: "order", fields: dt.order },
    { id: "store", title: "Store / Company", root: "store", fields: STORE_FIELD_LIST },
  ];
  if (dt.payments) groups.push({ id: "payments", title: "Payments (list)", root: "payments", list: true, fields: ["payment_mode_name", "payment_amount"] });
  groups.push({ id: "items", title: "Item row", root: "item", row: true, fields: dt.item });
  if (dt.batch) groups.push({ id: "batches", title: "Batch row (item + batch)", root: "item", row: true, fields: dt.batch });
  if (dt.key !== "stock_transfer") groups.push({ id: "tax", title: "GST summary row", root: "item", row: true, fields: ["item_gst", "taxable", "gst", "sgst", "igst"] });
  if (dt.creditNotes) groups.push({ id: "creditNotes", title: "Credit note row", root: "item", row: true, fields: ["invoice_number", "invoice_date", "invoice_total_amount", "claimed_amount", "adjusted_amount", "remaining_amount"] });
  if (dt.extra) Object.keys(dt.extra).forEach((k) => groups.push({ id: k, title: k === "fromStore" ? "From store" : k === "toStore" ? "To store" : k, root: `extra.${k}`, fields: dt.extra[k] }));
  return groups;
}
